<script setup>
import {useNotification} from '@kyvg/vue3-notification'
import {storeToRefs} from 'pinia'
import {ref, defineEmits, onMounted, defineProps, computed} from 'vue'
import TheSelect from '@shared-components/ui/selects/TheSelect.vue'
import {Modal as BaseModal} from 'bootstrap'
import BaseForm from '@shared-components/base/BaseForm.vue'
import {useAuthenticationStore} from '@/stores'
import {useCompanyBillingStore} from '@/views/finance/CompanyBillingStore'
import ApiErrors from '@shared-components/ui/ApiErrors.vue'
import dayjs from 'dayjs'
import {
  getCompanyInvoicePdf,
  getCurrencySign,
  hideElementIfExist,
  isProduction,
  removeObjectFromArray,
  showElementIfExist,
} from '@shared-utils/helpers'
import {pickDefaultLegalEntity} from '@shared-utils/quickBooksCustomers'
import {defaultPaymentTypeOptions, getPaymentTypeOptions} from '@/views/finance/PaymentRepeatables'
import PaymentMethodList from '@shared-components/finance/proforma-invoice/PaymentMethodList.vue'
import ProformaInvoiceSuccess from '@shared-components/finance/proforma-invoice/ProformaInvoiceSuccess.vue'
import TheTextarea from '@shared-components/ui/form/TheTextarea.vue'
import {errorNotify} from '@/services/notify/helpers'

const emit = defineEmits(['created', 'hidden', 'updated'])
const props = defineProps({
  id: {
    type: String,
    default: 'invoice-modal',
  },
  parentModalId: {
    type: String,
    default: '',
  },
  invoiceToEdit: {
    type: Object,
    default: null,
  },
  invoiceType: {
    type: [String, undefined],
    default: 'estimate',
  },
  companyHash: {
    type: String,
    default: null,
  },
})

const {userCompanyHash, user} = storeToRefs(useAuthenticationStore())
const {getAuthUserFullInfo} = useAuthenticationStore()
const {notify} = useNotification()
const {createInvoice, updateInvoice, getInvoiceInfo, getQuickBooksCustomers, getInvoiceRequisites, sendInvoiceEmail} =
  useCompanyBillingStore()

// Refs
const modal = ref()
const loading = ref(false)
const isInvoiceSent = ref(false)
const activeCompany = ref(null)
const error = ref(null)
const createdInvoice = ref(null)
const quickBooksCustomersOptions = ref([])
const quickBooksPaymentsList = ref([])
const isValidated = ref(true)
const hasPeriodDate = ref(false)
const clientInfo = ref(null)
const requisites = ref('')
const requisitesTag = ref(null)
const requisitesLoading = ref(false)
const productIds = ref({
  advertisingServices: isProduction() ? 13 : 28,
  couponIncome: isProduction() ? 1000101002 : 29,
  financeFee: isProduction() ? 1000101202 : 30,
  creativeStudio: isProduction() ? 1000101402 : 31,
})
const advertisingLineItemDescription =
  'Advertising Accounts up on the Client’s request Support related to provided Advertising Accounts ' +
  'Landing pages and Creatives Moderation.\n' +
  "Ads Creation upon the Client's request."

const defaultLineItem = {
  service_id: '',
  service_description: '',
  price: null,
}

const defaultFormData = {
  company_hash: props.companyHash ?? userCompanyHash.value,
  quick_books_customer_id: null,
  payment_type: undefined,
  invoice_date: dayjs().format('YYYY-MM-DD'),
  due_date: dayjs().add(1, 'day').format('YYYY-MM-DD'),
  send_email: false,
  send_to_email: '',
  period_from: undefined,
  period_to: undefined,
  send_to_managers: '',
  line_items: [],
  payments: [],
  type: props.invoiceType,
}

const defaultOptionalFormData = {
  period_from: dayjs().format('YYYY-MM-DD'),
  period_to: dayjs().add(1, 'day').format('YYYY-MM-DD'),
}
const optionalFormData = ref(cloneSafe(defaultOptionalFormData))
const formData = ref(cloneSafe(defaultFormData))
const currency = ref(getCurrencySign('USD'))
const productOptions = computed(() =>
  [
    {label: 'Advertising Services', value: productIds.value.advertisingServices},
    {label: 'Coupon Income', value: productIds.value.couponIncome},
    {label: 'Creative Studio', value: productIds.value.creativeStudio},
  ].filter(Boolean),
)
const isSendToManagers = ref(!!formData.value.send_to_managers)

// Computed
const selectedCustomer = computed(() =>
  quickBooksCustomersOptions.value.find(item => item?.value === formData.value.quick_books_customer_id),
)
const totalPrice = computed(() => {
  return formData.value.line_items.reduce((sum, item) => {
    return sum + (Number(item.price) || 0)
  }, 0)
})

const canAddLineItems = computed(() => formData.value.line_items.length < productOptions.value.length)
const isInvoiceCreated = computed(() => !!createdInvoice.value)

// Methods
onMounted(() => {
  const modalElement = document.getElementById(props.id)
  modal.value = new BaseModal(modalElement)

  modalElement.addEventListener('hidden.bs.modal', () => {
    emit('hidden')
  })

  const modalBackdrop = props.parentModalId ? 'static' : true

  modal.value = new BaseModal(modalElement, {backdrop: modalBackdrop})

  init()
})

const init = async () => {
  if (props.invoiceToEdit) {
    try {
      let invoice = await getInvoiceInfo(props.invoiceToEdit.id, {
        with: ['company', 'line_items'],
        type: props.invoiceType,
      })

      let companyHash = invoice.company.hash
      delete invoice.company

      formData.value = invoice
      formData.value.company_hash = companyHash

      await onCompanyChanged(formData.value.company_hash)
    } catch (error) {
      return notify({type: 'error', text: error})
    }
  } else if (formData.value.company_hash) {
    if (props.companyHash === null) {
      clientInfo.value = await getAuthUserFullInfo()
      activeCompany.value = cloneSafe(clientInfo?.value?.company)
    }
    await onCompanyChanged(formData.value.company_hash)
    initLineItems()
  } else {
    initLineItems()
  }

  handleOpenModal()
}

const handleSubmitInvoice = async () => {
  if (props.invoiceToEdit) {
    await handleEdit()

    return
  }

  formData.value.send_email = false

  await handleCreate()
}

const handleDownloadPdf = () => {
  getCompanyInvoicePdf(createdInvoice.value.hash)
}

const sendInvoiceTo = async recipients => {
  error.value = null
  loading.value = true

  try {
    await sendInvoiceEmail(createdInvoice.value.hash, recipients)
    isInvoiceSent.value = true
  } catch (responseError) {
    error.value = responseError?.data
  } finally {
    loading.value = false
  }
}

const handleSendEmail = async () => {
  await sendInvoiceTo(formData.value.send_to_email)
}

const hideParentModal = () => {
  hideElementIfExist(props.parentModalId)
}

const showParentModal = () => {
  showElementIfExist(props.parentModalId)
}

const handleOpenModal = () => {
  props.parentModalId ? hideParentModal() : ''

  openModal()
}
const handleCloseModal = () => {
  props.parentModalId ? showParentModal() : ''

  closeModal()
}

const openModal = () => {
  modal.value.show()
}

const closeModal = () => {
  modal.value.hide()
}

const initLineItems = () => {
  formData.value.line_items.push({
    service_id: productIds.value.advertisingServices,
    service_description: advertisingLineItemDescription,
    price: Math.trunc(activeCompany.value?.balance_info?.recommended_top_up_amount) || null,
  })
}

const handleCreate = async () => {
  loading.value = true

  try {
    let invoice = await createInvoice(getRequestParams())
    emit('created', invoice)

    createdInvoice.value = invoice
    error.value = null

    return invoice
  } catch (responseError) {
    error.value = responseError?.data

    return null
  } finally {
    loading.value = false
  }
}

const handleEdit = async () => {
  loading.value = true

  try {
    await updateInvoice(getRequestParams())
  } catch (responseError) {
    return (error.value = responseError?.data)
  } finally {
    loading.value = false
  }

  emit('updated')
  handleCloseModal()
  notify({type: 'success', text: 'Invoice updated'})
}

const getRequestParams = () => {
  let params

  if (hasPeriodDate.value) {
    params = cloneSafe(formData.value)
    params.period_from = optionalFormData.value.period_from
    params.period_to = optionalFormData.value.period_to
  } else {
    params = cloneSafe(formData.value)
    params.period_from = null
    params.period_to = null
  }

  params.line_items.forEach(item => {
    let ioText = ''

    if (item.service_id === productIds.value.advertisingServices) {
      ioText = selectedCustomer.value?.io ? `Advertising Agreement No ${selectedCustomer.value.io}.\n` : ''
    }

    const periodText =
      hasPeriodDate.value && optionalFormData.value.period_from && optionalFormData.value.period_to
        ? `Period - ${dayjs(optionalFormData.value.period_from).format('YYYY/MM/DD')} - ${dayjs(
            optionalFormData.value.period_to,
          ).format('YYYY/MM/DD')}`
        : ''

    item.service_description = item.service_description.trim() + '\n' + ioText + periodText
  })

  if (Array.isArray(params.payments) && params.payments.length > 0) {
    let remaining = totalPrice.value

    params.payments = params.payments
      .map(item => {
        const unapplied = Number(item.unapplied_amount) || 0

        if (remaining <= 0) {
          return null
        }

        const appliedAmount = Math.min(unapplied, remaining)
        remaining -= appliedAmount

        return {
          payment_id: +item.id,
          amount: Number(appliedAmount.toFixed(2)),
        }
      })
      .filter(Boolean)
  }

  if (!isSendToManagers.value) {
    params.send_to_managers = undefined
  }

  params.with = ['company']
  params.type = props.invoiceType
  params.currency = defaultPaymentTypeOptions.find(item => item.value === formData.value.payment_type)?.currency

  return params
}

const resetPaymentType = () => {
  formData.value.payment_type = undefined
  requisites.value = ''
  requisitesTag.value = null
}

const setCurrency = () => {
  const paymentType = defaultPaymentTypeOptions.find(item => item.value === formData.value.payment_type)

  if (paymentType) {
    currency.value = getCurrencySign(paymentType.currency)
  }
}

const onPaymentTypeChange = async () => {
  setCurrency()

  requisitesLoading.value = true

  try {
    const paymentRequisites = await getInvoiceRequisites(formData.value.payment_type)
    requisites.value = paymentRequisites.requisites
    requisitesTag.value = paymentRequisites.tag
  } catch (error) {
    requisites.value = ''
    requisitesTag.value = null
    errorNotify(notify, error)
  } finally {
    requisitesLoading.value = false
  }
}

const onCompanyChanged = async companyHash => {
  resetPaymentType()

  quickBooksPaymentsList.value = []
  formData.value.payments = []

  if (!companyHash) {
    return (quickBooksCustomersOptions.value = [])
  }

  const customers = await getQuickBooksCustomers(companyHash)

  quickBooksCustomersOptions.value = customers.map(({id, company_name, email, io, io_company}) => ({
    value: id,
    label: company_name,
    email,
    io,
    io_company,
  }))

  const defaultCustomer = pickDefaultLegalEntity(customers)

  formData.value.quick_books_customer_id = defaultCustomer?.id ?? null
  formData.value.send_to_email = getInvoiceRecipientEmail(defaultCustomer)

  setSendToManagersField()

  if (!formData.value.send_to_managers) {
    isSendToManagers.value = false
  }
  if (!formData.value.send_to_email) {
    formData.value.send_email = false
  }
}

const getInvoiceRecipientEmail = entity => {
  return entity?.email || user.value?.email || ''
}

const setSendToManagersField = () => {
  if (!activeCompany.value) {
    return
  }

  const managerRoles = ['csm', 'csm_2', 'media_planner', 'media_planner_2']

  const emails = managerRoles.map(role => activeCompany.value[role]?.email).filter(email => !!email)

  const uniqueEmails = [...new Set(emails)]

  formData.value.send_to_managers = uniqueEmails.join(', ')
}

const addNewLineItem = () => {
  if (canAddLineItems.value) {
    formData.value.line_items.push({
      ...cloneSafe(defaultLineItem),
      price: null,
    })
  }
}

const setServiceDescription = service_id => {
  const mapper = {
    [productIds.value.advertisingServices]: advertisingLineItemDescription,
    [productIds.value.couponIncome]: '',
    [productIds.value.financeFee]: 'Finance fee of 0.5% based on the actual usage of the line of credit',
    [productIds.value.creativeStudio]: '',
  }

  return mapper[service_id] || ''
}

const onLineItemServiceChange = (service_id, index) => {
  formData.value.line_items[index].service_description = setServiceDescription(service_id)
  formData.value.line_items[index].price = null
}

const getProductOptions = currentServiceId => {
  const selectedIds = formData.value.line_items
    .map(item => Number(item.service_id))
    .filter(id => id !== Number(currentServiceId))

  return productOptions.value.filter(
    option => !selectedIds.includes(option.value) || option.value === Number(currentServiceId),
  )
}

const onPriceChange = () => {
  const totalPrice = formData.value.line_items.reduce((sum, item) => sum + (item?.price || 0), 0)

  const isValid = totalPrice > 0

  error.value = isValid
    ? null
    : {
        message: 'The total invoice amount needs to be more than zero.',
      }

  isValidated.value = isValid
}

const onPriceKeydown = (e, item) => {
  if (item.service_id !== 29 && e.key === '-') {
    e.preventDefault()
  }
}

const onEntityChange = () => {
  formData.value.send_to_email = getInvoiceRecipientEmail(selectedCustomer.value)
  resetPaymentType()
}
</script>

<template>
  <div class="modal fade" :id="props.id" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog max-w-1000-px">
      <div class="modal-content">
        <div v-if="!isInvoiceCreated" class="modal-header custom px-7 py-5">
          <h5 class="modal-title mb-0 custom fs-6">
            <template v-if="!props.invoiceToEdit">New Proforma Invoice</template>
            <template v-else>
              Edit invoice #<b>{{ formData.id }}</b>
            </template>
          </h5>
          <button type="button" class="btn-close" aria-label="Close" @click="handleCloseModal" />
        </div>
        <button
          v-else
          type="button"
          class="btn-close proforma-close custom p-7"
          aria-label="Close"
          @click="handleCloseModal"
        />
        <base-form @on-submitted="handleSubmitInvoice">
          <div class="modal-body custom p-7">
            <div v-if="!isInvoiceCreated">
              <div class="form-group custom mb-7">
                <label class="custom fs-5 mb-4">Legal Entity</label>
                <the-select
                  v-model="formData.quick_books_customer_id"
                  :options="quickBooksCustomersOptions"
                  :disabled="loading"
                  :allow_clear="false"
                  @change="onEntityChange"
                  id="invoice-quick_books_customer_id"
                  placeholder="Select Legal Entity"
                  required
                />
              </div>

              <h5 class="proforma-title custom fs-5 mb-5">Services</h5>
              <div class="d-flex custom gap-4">
                <div class="w-230-px flex-shrink-0">
                  <label>Activity</label>
                </div>
                <div class="w-150-px flex-shrink-0">
                  <label>Amount</label>
                </div>
                <div class="flex-grow-1">
                  <label>Description</label>
                </div>
              </div>
              <div
                class="form-group d-flex custom gap-4 mb-4"
                v-for="(item, index) in formData.line_items"
                :key="index"
              >
                <div class="w-230-px flex-shrink-0">
                  <the-select
                    v-model="item.service_id"
                    :options="getProductOptions(item.service_id)"
                    :allow_clear="false"
                    :can_deselect="false"
                    @change="onLineItemServiceChange($event, index)"
                    placeholder="Product/Service"
                    class="w-100"
                    :preselect="false"
                    :disabled="loading"
                    required
                  />
                </div>
                <div class="w-150-px flex-shrink-0">
                  <div class="input-group">
                    <div class="input-group-text">{{ currency }}</div>
                    <input
                      v-model.number="item.price"
                      class="form-control"
                      type="number"
                      step="0.01"
                      :min="item.service_id === 29 ? undefined : 0"
                      :disabled="loading"
                      @keydown="onPriceKeydown($event, item)"
                      @input="onPriceChange"
                      required
                    />
                  </div>
                </div>
                <div class="flex-grow-1">
                  <the-textarea
                    v-model="item.service_description"
                    class="form-control no-resize"
                    placeholder="Services Provided by Point2Web as Agreed"
                    :disabled="loading"
                    :id="`service_description_${index}`"
                    rows="1"
                    required
                  />
                </div>
                <div v-if="formData.line_items.length > 1" class="flex-shrink-0">
                  <div class="input-height">
                    <button
                      class="form-icon-btn"
                      @click="removeObjectFromArray(index, formData.line_items)"
                      :disabled="loading"
                      type="button"
                    >
                      <i class="fa-light fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
              <button
                v-if="canAddLineItems"
                @click="addNewLineItem"
                class="btn btn-secondary d-flex align-items-center gap-1"
                type="button"
                :disabled="loading"
              >
                <img src="@shared-images/icons/button/plus.svg" alt="plus" /> <span>Add item</span>
              </button>

              <template v-if="!invoiceToEdit">
                <h5 class="proforma-title custom fs-5 mt-7 mb-5">Payment Methods</h5>
                <payment-method-list
                  v-model="formData.payment_type"
                  :options="getPaymentTypeOptions(selectedCustomer?.io_company)"
                  :disabled="loading || !formData.quick_books_customer_id"
                  @change="onPaymentTypeChange"
                />
              </template>
            </div>

            <proforma-invoice-success
              v-else
              v-model:email="formData.send_to_email"
              :payment-type="formData.payment_type"
              :requisites="requisites"
              :requisites-tag="requisitesTag"
              :requisites-loading="requisitesLoading"
              :disabled="loading"
              :email-sent="isInvoiceSent"
              :with-managers="false"
              @download="handleDownloadPdf"
              @send-email="handleSendEmail"
            />

            <api-errors v-if="error" :error="error" class="mt-3" />
          </div>
          <div v-if="!isInvoiceCreated" class="modal-footer justify-content-end custom gap-4 px-7 py-4">
            <button type="button" class="btn btn-secondary" @click="handleCloseModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="loading || !isValidated">
              <template v-if="invoiceToEdit">Save</template>
              <template v-else>Generate</template>
            </button>
          </div>
        </base-form>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.proforma-close {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
}

.proforma-title {
  font-weight: 600;
  line-height: 1.4;
  color: $black;
}

.qb-payments-table {
  display: flex;
  flex-direction: column;
}

.qb-payments-table-row {
  padding: 10px 16px;

  &:last-child {
    border-bottom: none;
  }
}

.text-dark-grey {
  color: #4b5563;
}
</style>
